import {
  Button,
  Icon,
  Input,
  Tag,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { EnumOrganizationEmailDomainStatus } from "gql/generated";
import { CheckCircle, Copy, InfoCircle, WarningTriangle } from "iconoir-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "shared/components";
import { InlineSectionTitle } from "shared/components/atoms/InlineSectionTitle";
import { Flex } from "styled-system/jsx";
import { useCustomEmailDomainPanel } from "./useCustomEmailDomainPanel";

type DnsRecordRowProps = {
  label: string;
  type: string;
  host: string;
  data: string;
  valid?: boolean;
};

const DnsRecordRow = ({
  label,
  type,
  host,
  data,
  valid,
}: DnsRecordRowProps) => (
  <Flex
    flexDir="column"
    gap="100"
    p="250"
    bgColor="bg.wa.lightest"
    border="1px solid"
    borderColor="border.light"
    borderRadius="150"
  >
    <Flex justifyContent="space-between" alignItems="center">
      <Text visual="smallSemiBold" color="text.dark">
        {label}
      </Text>
      <Tag visual={valid ? "success" : "warning"} mode="light" size="sm">
        {valid ? "Verified" : "Pending"}
      </Tag>
    </Flex>
    <Flex flexDir="column" gap="50">
      <Flex alignItems="center" gap="100">
        <Text visual="smallRegular" color="text.mid" w="60px" flexShrink={0}>
          Type
        </Text>
        <Text visual="smallCode" color="text.dark">
          {type.toUpperCase()}
        </Text>
      </Flex>
      <Flex alignItems="center" gap="100">
        <Text visual="smallRegular" color="text.mid" w="60px" flexShrink={0}>
          Host
        </Text>
        <CopyToClipboard text={host} visual="smallCode" color="text.dark" />
      </Flex>
      <Flex alignItems="center" gap="100">
        <Text visual="smallRegular" color="text.mid" w="60px" flexShrink={0}>
          Value
        </Text>
        <CopyToClipboard text={data} visual="smallCode" color="text.dark" />
      </Flex>
    </Flex>
  </Flex>
);

type DnsSettingsRecord = {
  label: string;
  type: string;
  host: string;
  data: string;
};

const formatDnsSettings = (intro: string, records: DnsSettingsRecord[]) =>
  [
    intro,
    ...records.map(record =>
      [
        record.label,
        `Type: ${record.type.toUpperCase()}`,
        `Host: ${record.host}`,
        `Value: ${record.data}`,
      ].join("\n"),
    ),
  ].join("\n\n");

const STATUS_VISUAL: Record<
  EnumOrganizationEmailDomainStatus,
  "neutral" | "warning" | "success" | "destructive"
> = {
  [EnumOrganizationEmailDomainStatus.Requested]: "neutral",
  [EnumOrganizationEmailDomainStatus.DnsPending]: "warning",
  [EnumOrganizationEmailDomainStatus.Active]: "success",
  [EnumOrganizationEmailDomainStatus.Disabled]: "destructive",
};

export const CustomEmailDomainPanel = () => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.LOGO.CUSTOM_EMAIL_DOMAIN",
  });
  const { t: tCore } = useTranslation();
  const { toastFactory } = useToastContext();
  const [copiedAll, setCopiedAll] = useState(false);
  const {
    flagEnabled,
    prerequisitesMet,
    config,
    fromAddress,
    setFromAddress,
    emailIsValid,
    canRequest,
    isSubmitting,
    onSubmit,
  } = useCustomEmailDomainPanel();

  const handleCopyAllSettings = async () => {
    if (!config?.dnsRecords) return;
    const text = formatDnsSettings(
      t("COPY_ALL_INTRO", { fromAddress: config.fromAddress }),
      [
        {
          label: "Mail CNAME",
          type: config.dnsRecords.mailCname.type,
          host: config.dnsRecords.mailCname.host,
          data: config.dnsRecords.mailCname.data,
        },
        {
          label: "DKIM 1",
          type: config.dnsRecords.dkim1.type,
          host: config.dnsRecords.dkim1.host,
          data: config.dnsRecords.dkim1.data,
        },
        {
          label: "DKIM 2",
          type: config.dnsRecords.dkim2.type,
          host: config.dnsRecords.dkim2.host,
          data: config.dnsRecords.dkim2.data,
        },
      ],
    );
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAll(true);
      window.setTimeout(() => setCopiedAll(false), 1500);
    } catch {
      toastFactory.create({
        title: tCore("core:ERROR.TITLE"),
        description: t("COPY_ALL_ERROR"),
      });
    }
  };

  if (!flagEnabled) return null;

  return (
    <Flex
      flexDir="column"
      gap="250"
      pt="400"
      borderTop="1px solid"
      borderTopColor="border.light"
    >
      <InlineSectionTitle title={t("TITLE")} />
      <Text color="text.mid">{t("DESCRIPTION")}</Text>

      {!prerequisitesMet ? (
        <Flex
          gap="150"
          p="300"
          bgColor="bg.wa.lightest"
          border="1px solid"
          borderColor="border.light"
          borderRadius="200"
          alignItems="flex-start"
        >
          <Icon Element={InfoCircle} color="icon.mid" flexShrink={0} />
          <Text color="text.mid">{t("PREREQUISITE_NOTICE")}</Text>
        </Flex>
      ) : (
        <Flex flexDir="column" gap="300">
          {config && (
            <Flex
              flexDir="column"
              gap="200"
              p="300"
              bgColor="bg.wa.lightest"
              border="1px solid"
              borderColor="border.light"
              borderRadius="200"
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Flex flexDir="column" gap="50">
                  <Text visual="bodySemiBold" color="text.dark">
                    {config.fromAddress}
                  </Text>
                  <Text visual="smallRegular" color="text.mid">
                    {t(`STATUS.${config.status}.LABEL`)}
                  </Text>
                </Flex>
                <Tag visual={STATUS_VISUAL[config.status]} mode="light">
                  {t(`STATUS.${config.status}.TAG`)}
                </Tag>
              </Flex>

              <Text color="text.mid">
                {t(`STATUS.${config.status}.DESCRIPTION`)}
              </Text>

              {config.status === EnumOrganizationEmailDomainStatus.DnsPending &&
                config.dnsRecords && (
                  <Flex flexDir="column" gap="150" pt="100">
                    <DnsRecordRow
                      label="Mail CNAME"
                      type={config.dnsRecords.mailCname.type}
                      host={config.dnsRecords.mailCname.host}
                      data={config.dnsRecords.mailCname.data}
                      valid={
                        config.validationResults?.mailCname?.valid ??
                        config.dnsRecords.mailCname.valid
                      }
                    />
                    <DnsRecordRow
                      label="DKIM 1"
                      type={config.dnsRecords.dkim1.type}
                      host={config.dnsRecords.dkim1.host}
                      data={config.dnsRecords.dkim1.data}
                      valid={
                        config.validationResults?.dkim1?.valid ??
                        config.dnsRecords.dkim1.valid
                      }
                    />
                    <DnsRecordRow
                      label="DKIM 2"
                      type={config.dnsRecords.dkim2.type}
                      host={config.dnsRecords.dkim2.host}
                      data={config.dnsRecords.dkim2.data}
                      valid={
                        config.validationResults?.dkim2?.valid ??
                        config.dnsRecords.dkim2.valid
                      }
                    />
                    <Button
                      type="button"
                      mode="light"
                      size="sm"
                      Icon={Copy}
                      w="fit-content"
                      onClick={() => void handleCopyAllSettings()}
                    >
                      {copiedAll ? t("COPIED") : t("COPY_ALL_SETTINGS")}
                    </Button>
                  </Flex>
                )}

              {config.status === EnumOrganizationEmailDomainStatus.Active && (
                <Flex gap="100" alignItems="center">
                  <Icon Element={CheckCircle} color="icon.success.mid" />
                  <Text visual="smallMedium" color="text.mid">
                    {t("STATUS.ACTIVE.CONFIRMATION")}
                  </Text>
                </Flex>
              )}

              {config.status === EnumOrganizationEmailDomainStatus.Disabled && (
                <Flex gap="100" alignItems="center">
                  <Icon Element={WarningTriangle} color="icon.warning.mid" />
                  <Text visual="smallMedium" color="text.mid">
                    {t("STATUS.DISABLED.NOTICE")}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}

          {canRequest && (
            <Flex
              flexDir="column"
              gap="200"
              p="300"
              bgColor="bg.wa.lightest"
              border="1px solid"
              borderColor="border.light"
              borderRadius="200"
            >
              <Input
                name="customEmailFromAddress"
                label={t("FROM_ADDRESS_LABEL")}
                hint={t("FROM_ADDRESS_HINT")}
                placeholder={t("FROM_ADDRESS_PLACEHOLDER")}
                value={fromAddress}
                onChange={e => setFromAddress(e.target.value)}
              />
              <Flex justifyContent="flex-end">
                <Button
                  type="button"
                  visual="primary"
                  onClick={onSubmit}
                  disabled={
                    !fromAddress.trim() || !emailIsValid || isSubmitting
                  }
                  loading={isSubmitting}
                >
                  {config ? t("RESUBMIT") : t("SUBMIT")}
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};
